import { useMemo } from 'react';
import { createPortal } from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import { zIndex } from '@cbhq/cds-common/tokens/zIndex';
import { useTheme } from '@cbhq/cds-web/hooks/useTheme';
import { VStack } from '@cbhq/cds-web/layout';
import { ThemeProvider } from '@cbhq/cds-web/system';
import { TextBody, TextTitle1 } from '@cbhq/cds-web/typography';
import { ActionType, AnalyticsEventImportance, ComponentType } from '@cbhq/client-analytics';
import type { CoinbaseProfileMenuResource } from '@cbhq/identity-api-hooks/profile-menu/types';

import { useLogEventOnMount } from '../hooks/useLogEventOnMount';

import { EntityPickerRow } from './EntityPickerRow';

const messages = defineMessages({
  title: {
    id: 'EntityPickerFullScreen.title',
    defaultMessage: 'Choose an account',
    description: 'Title for the full-screen entity picker that asks the user to select an account',
  },
  subtitle: {
    id: 'EntityPickerFullScreen.subtitle',
    defaultMessage: 'Select an account to continue.',
    description: 'Subtitle for the entity picker providing additional context',
  },
});

type EntityPickerFullScreenProps = {
  resources: CoinbaseProfileMenuResource[];
  onSelect: (resourceId: string) => void;
};

export function EntityPickerFullScreen({ resources, onSelect }: EntityPickerFullScreenProps) {
  const { formatMessage } = useIntl();
  const theme = useTheme();

  const viewEventData = useMemo(
    () => ({
      action: ActionType.render,
      componentType: ComponentType.page,
      resource_count: resources.length,
      legal_entity_count: resources.filter((r) => r.resourceType === 'legal_entity').length,
      user_count: resources.filter((r) => r.resourceType === 'user').length,
    }),
    [resources],
  );

  useLogEventOnMount('entity_picker_fullscreen_view', viewEventData, AnalyticsEventImportance.high);

  // Portal to document.body to escape the navbar's stacking context (on www the
  // sidebar has a higher z-index than the navbar). That renders this subtree
  // outside the app's themed DOM, so we re-establish a ThemeProvider to emit the
  // theme CSS variables (var(--cds-*)) every CDS component depends on.
  //
  // A nested cds-web v9 ThemeProvider only emits the variables that DIFFER from
  // the parent theme in React context. Because `theme` comes straight from
  // useTheme(), that diff is empty and the portal would render unstyled — so we
  // pass the `isolated` prop, which forces the ThemeProvider to emit the full
  // variable set even when rendered outside its parent's DOM tree (e.g. a portal).
  return createPortal(
    <ThemeProvider theme={theme} activeColorScheme={theme.activeColorScheme} isolated>
      <VStack
        position="fixed"
        width="100vw"
        height="100vh"
        zIndex={zIndex.portal}
        left={0}
        top={0}
        background="bg"
        alignItems="center"
        paddingTop={10}
        testID="entity-picker-fullscreen"
      >
        <VStack width="100%" maxWidth={480} gap={3}>
          <VStack paddingX={3} gap={1}>
            <TextTitle1 as="h1">{formatMessage(messages.title)}</TextTitle1>
            <TextBody color="fgMuted">{formatMessage(messages.subtitle)}</TextBody>
          </VStack>
          <VStack overflow="auto">
            {resources.map((resource) => (
              <EntityPickerRow
                key={resource.identityAccountId}
                resource={resource}
                onPress={onSelect}
              />
            ))}
          </VStack>
        </VStack>
      </VStack>
    </ThemeProvider>,
    document.body,
  );
}
